import { useState } from 'react';
import Button from '../common/Button';
import Alert from '../common/Alert';

const EMPTY_OPTION = { option_text: '', is_correct: false };
const TRUE_FALSE_OPTIONS = [
  { option_text: 'True', is_correct: true, order_index: 0 },
  { option_text: 'False', is_correct: false, order_index: 1 },
];

const DEFAULT_FORM = {
  question_text: '',
  question_type: 'mcq',
  marks: 1,
  section: '',
  explanation: '',
  correct_text_answer: '',
  case_sensitive_match: false,
  options: [
    { option_text: '', is_correct: true,  order_index: 0 },
    { option_text: '', is_correct: false, order_index: 1 },
    { option_text: '', is_correct: false, order_index: 2 },
    { option_text: '', is_correct: false, order_index: 3 },
  ],
};

const inputCls = 'w-full px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 bg-white placeholder:text-slate-400 hover:border-slate-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20 focus:outline-none transition-all duration-150 resize-none';

const QuestionForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || DEFAULT_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleTypeChange = (type) => {
    let options = form.options;
    if (type === 'true_false') {
      options = TRUE_FALSE_OPTIONS;
    } else if (type === 'short_answer' || type === 'fill_in_the_blank') {
      options = [];
    } else if (form.question_type === 'true_false' || form.question_type === 'short_answer' || form.question_type === 'fill_in_the_blank') {
      options = DEFAULT_FORM.options;
    }
    setForm({ ...form, question_type: type, options });
  };

  const setOption = (i, field, value) => {
    const opts = form.options.map((o, idx) => {
      if (idx === i) return { ...o, [field]: value };
      if (field === 'is_correct' && value) return { ...o, is_correct: false };
      return o;
    });
    setForm({ ...form, options: opts });
  };

  const addOption = () => setForm({
    ...form,
    options: [...form.options, { ...EMPTY_OPTION, order_index: form.options.length }],
  });

  const removeOption = (i) => setForm({
    ...form,
    options: form.options.filter((_, idx) => idx !== i).map((o, idx) => ({ ...o, order_index: idx })),
  });

  const handleSave = async () => {
    if (!form.question_text.trim()) { setError('Question text is required'); return; }

    const type = form.question_type;
    if (type === 'mcq' || type === 'true_false') {
      if (!form.options.some((o) => o.is_correct)) { setError('At least one option must be marked correct'); return; }
      if (form.options.some((o) => !o.option_text.trim())) { setError('All options must have text'); return; }
    }
    if (type === 'fill_in_the_blank' && !form.correct_text_answer?.trim()) {
      setError('A correct answer is required for fill-in-the-blank questions');
      return;
    }

    setError('');
    setSaving(true);
    try { await onSave(form); }
    catch (e) { setError(e.response?.data?.message || 'Save failed'); setSaving(false); }
  };

  const optionLetters = 'ABCDEF';
  const type = form.question_type;
  const showOptions = type === 'mcq' || type === 'true_false';

  return (
    <div className="space-y-4">
      {error && <Alert type="error">{error}</Alert>}

      {/* Question type */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Question Type</label>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'mcq', label: 'Multiple Choice' },
            { value: 'true_false', label: 'True / False' },
            { value: 'fill_in_the_blank', label: 'Fill in the Blank' },
            { value: 'short_answer', label: 'Short Answer' },
          ].map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => handleTypeChange(t.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                form.question_type === t.value
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-primary-300'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Question text */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Question Text</label>
        <textarea
          rows={3}
          className={inputCls}
          value={form.question_text}
          onChange={(e) => setForm({ ...form, question_text: e.target.value })}
          placeholder="Enter the question…"
        />
      </div>

      {/* Marks */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-slate-700">Marks:</label>
        <input
          type="number"
          min="0.5"
          step="0.5"
          className="w-20 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400"
          value={form.marks}
          onChange={(e) => setForm({ ...form, marks: parseFloat(e.target.value) || 1 })}
        />
      </div>

      {/* Section */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-slate-700 whitespace-nowrap">Section:</label>
        <input
          type="text"
          className="flex-1 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400"
          value={form.section || ''}
          onChange={(e) => setForm({ ...form, section: e.target.value })}
          placeholder="e.g. Section A (optional)"
        />
      </div>

      {/* MCQ / True-False Options */}
      {showOptions && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Options
            <span className="text-slate-400 text-xs font-normal ml-2">Select the correct answer</span>
          </label>
          <div className="space-y-2">
            {form.options.map((opt, i) => (
              <div key={i} className={`flex items-center gap-2.5 p-2.5 rounded-xl border transition-colors ${
                opt.is_correct ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'
              }`}>
                <button
                  type="button"
                  onClick={() => type !== 'true_false' && setOption(i, 'is_correct', true)}
                  className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    opt.is_correct ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 hover:border-emerald-400'
                  }`}
                >
                  {opt.is_correct ? (
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-xs text-slate-400 font-bold">{optionLetters[i]}</span>
                  )}
                </button>

                <input
                  type="text"
                  placeholder={`Option ${optionLetters[i]}`}
                  className="flex-1 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
                  value={opt.option_text}
                  onChange={(e) => setOption(i, 'option_text', e.target.value)}
                  readOnly={type === 'true_false'}
                />

                {type === 'mcq' && form.options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeOption(i)}
                    className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {type === 'mcq' && form.options.length < 6 && (
            <button
              type="button"
              onClick={addOption}
              className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Option
            </button>
          )}
        </div>
      )}

      {/* Fill in the blank — correct answer */}
      {type === 'fill_in_the_blank' && (
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Correct Answer</label>
          <input
            type="text"
            className={inputCls}
            value={form.correct_text_answer || ''}
            onChange={(e) => setForm({ ...form, correct_text_answer: e.target.value })}
            placeholder="Expected answer…"
          />
          <label className="flex items-center gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.case_sensitive_match || false}
              onChange={(e) => setForm({ ...form, case_sensitive_match: e.target.checked })}
              className="w-4 h-4 accent-primary-600"
            />
            <span className="text-xs text-slate-600">Case-sensitive matching</span>
          </label>
        </div>
      )}

      {/* Short answer — info only */}
      {type === 'short_answer' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
          <p className="text-xs text-blue-700">Short answer questions are graded manually. You can grade responses from the <strong>Grade</strong> tab on the exam detail page.</p>
        </div>
      )}

      {/* Explanation */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Explanation
          <span className="text-slate-400 text-xs font-normal ml-1">(optional)</span>
        </label>
        <textarea
          rows={2}
          className={inputCls}
          value={form.explanation}
          onChange={(e) => setForm({ ...form, explanation: e.target.value })}
          placeholder="Explain the correct answer…"
        />
      </div>

      <div className="flex gap-3 pt-1">
        <Button onClick={handleSave} loading={saving}>Save Question</Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

export default QuestionForm;
